import { CODE_ALPHABET, CODE_LENGTH } from './constants.ts';
import type { ClientView, Locale, ReactionId, RoomSettings, RumorDraft, Vote } from './types.ts';

export type ErrorCode =
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'GAME_IN_PROGRESS'
  | 'NAME_INVALID'
  | 'NAME_TAKEN'
  | 'SESSION_INVALID'
  | 'NOT_HOST'
  | 'WRONG_PHASE'
  | 'NOT_ENOUGH_PLAYERS'
  | 'ADULTS_REQUIRED'
  | 'BOTS_DISABLED'
  | 'INVALID_INPUT'
  | 'RATE_LIMITED';

export type AckResult<T = null> = { ok: true; data: T } | { ok: false; error: ErrorCode };

export interface Session {
  code: string;
  playerId: string;
  token: string;
}

/** Every request a player can make, with its payload and the data its reply carries. */
export interface Requests {
  'room:create': { payload: { name: string; locale: Locale }; result: Session };
  'room:join': { payload: { name: string }; result: Session };
  'room:resume': { payload: Session; result: Session };
  'room:leave': { payload: null; result: null };
  'lobby:settings': { payload: Partial<RoomSettings>; result: null };
  'lobby:portrait': { payload: null; result: null };
  'lobby:kick': { payload: { playerId: string }; result: null };
  'lobby:add-bot': { payload: null; result: null };
  'game:start': { payload: null; result: null };
  'confessions:update': { payload: { answers: string[]; done: boolean }; result: null };
  'writing:update': { payload: { drafts: RumorDraft[]; done: boolean }; result: null };
  'investigation:vote': { payload: { articleId: string } & Partial<Vote>; result: null };
  'investigation:react': { payload: { articleId: string; reaction: ReactionId }; result: null };
  'plume:vote': { payload: { articleId: string }; result: null };
  'host:advance': { payload: null; result: null };
  'host:restart': { payload: null; result: null };
}

export type RequestName = keyof Requests;

/** A request frame. The server answers each `id` with exactly one `reply`. */
export type ClientMessage = { [K in RequestName]: { id: number; type: K; payload: Requests[K]['payload'] } }[RequestName];

export type ServerMessage =
  | { type: 'reply'; id: number; result: AckResult<unknown> }
  | { type: 'state'; view: ClientView }
  | { type: 'kicked' };

/** Largest frame the server reads; drafts are the biggest payload and stay far below it. */
export const MAX_MESSAGE_BYTES = 64 * 1024;

/**
 * Each room is its own WebSocket endpoint: `/api/rooms/ABCD/ws` joins or resumes one,
 * `/api/rooms/new/ws` opens a fresh one under a code the server picks.
 */
export const ROOM_SOCKET_PATH = /^\/api\/rooms\/([A-Za-z]+)\/ws$/;
export const NEW_ROOM = 'new';

export function normalizeCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  if (code.length !== CODE_LENGTH) return null;
  return [...code].every((letter) => CODE_ALPHABET.includes(letter)) ? code : null;
}

/** Served by `GET /api/config` so the client can build join links and QR codes. */
export interface PublicConfig {
  publicUrl: string | null;
  botsEnabled: boolean;
  maxPlayers: number;
}
